import Component from '@glimmer/component'
import { action } from '@ember/object'
import {
  serialize as serializeSort,
  deserialize as deserializeSort,
  updateSortColumns,
} from '@blakeelearning/data-tables/utils/sorting'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { on } from '@ember/modifier'
import { eq } from 'ember-truth-helpers'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'

interface Args {
  /**
   * A sort query param string value. This value will be deserialized into a sorting config.
   * Examples:
   *  first_name
   *  -last_name
   *  first_name,-last_name
   */
  sort: string
  /**
   * The sort key for this header cell. On clicking the header, this sort key will be used to adjust the sort query.
   */
  sortKey: string
  /**
   * This function will be called with the updated sort query param string, intended to be directly applied to the query
   * param.
   */
  updateSort: (newSort: string) => void
  tooltip?: string
}

interface Signature {
  Element: HTMLTableCellElement
  Args: Args
  Blocks: {
    default: []
  }
}

export class Sortable extends Component<Signature> {
  get sortingConfig() {
    return deserializeSort(this.args.sort)
  }

  @action
  onSort() {
    const newSortingConfig = updateSortColumns(this.sortingConfig, this.args.sortKey)
    const newSort = serializeSort(newSortingConfig)
    this.args.updateSort(newSort)
  }

  get firstSortKey() {
    return this.sortingConfig?.columns[0]?.key
  }

  get firstSortIcon() {
    return this.sortingConfig?.columns[0]?.sortDirection === 'asc'
      ? 'phosphor-caret-up-fill'
      : 'phosphor-caret-down-fill'
  }

  <template>
    <th data-test-sort-key={{@sortKey}} ...attributes>
      {{! div with role button, rather than button element, to allow flex to work and take up full width of th cell}}
      <div role="button" class="flex items-center gap-1 p-3 font-semibold" {{on "click" this.onSort}}>
        {{yield}}
        {{#if @tooltip}}
          <span>
            <PhosphorIcon @icon="phosphor-info-fill" role="presentation" />
            <Tooltip @text={{@tooltip}} />
          </span>
        {{/if}}
        <PhosphorIcon
          class="ml-auto"
          @icon={{if (eq this.firstSortKey @sortKey) this.firstSortIcon "phosphor-caret-up-down-fill"}}
          role="presentation"
        />
      </div>
    </th>
  </template>
}

export default Sortable
